import React, { useEffect, useState } from "react";
import OrderService from "../../services/OrderService";
import TrackOrderV4 from "./v4/TrackOrder";
import TrackOrderV3 from "./v3/TrackOrder";
import UserService from "../../services/UserService";
import Analytics, { TRACK_PAGE_CATEGORIES } from "../../lib/anayltics";
import Page from "../../components/Page";
import { IonHeader, IonIcon, IonSpinner } from "@ionic/react";
import styled from "styled-components";
import ErrorComponent from "../../components/Error";
import { KitchenVersions } from "../../constants/XookConstants";
import { connect } from "react-redux";
import withNav from "../../utils/hoc/withNav";
import { bindActionCreators } from "@reduxjs/toolkit";
import { setUserData } from "../../actions/UserActions";
import { NavLink } from "react-router-dom";
import { chevronBackOutline } from "ionicons/icons";
import AsyncContentWrapper from "../../components/AsyncContentWrapper";
import Dropdown from "../../components/Dropdown";
import { PrimaryText, PrimaryTextLight } from "../../components/Texts";
import { isEmptyObject } from "../../utils/objects";
import { getOrderCode } from "../../utils/misc";

const Center = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

const PageHeader = styled(IonHeader)`
	position: sticky;
	margin: 0 0 0 0;
	top: 0;
	background: ${({ theme }) => theme.colors.main.background};
	font-family: inherit;
	font-style: normal;
	font-size: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1rem;
	border: none;
	box-shadow: none;
	&:after {
		background-image: none;
	}
`;

const PaddedElement = styled.div`
	margin-top: 5rem;
`;

const BackButton = styled(NavLink)``;

const BackButtonImg = styled(IonIcon)`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 24px;
	height: 24px;
	color: #fff;
	/* Icon alignment */
	margin-left: -5px;
`;

const PageTitle = styled.div`
	/* Keeping it centered becuase of icon alignment */
	padding-left: 5px;
	font-style: normal;
	font-weight: 900;
	font-size: 20px;
	letter-spacing: 0px;
	display: flex;
	justify-content: center;
`;

const HelpButton = styled(IonIcon)`
	width: 20px;
	height: 20px;
	margin-left: auto;
`;

const Box = styled.div<any>`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 0.5rem 1rem 0 1rem;
`;


function mapKitchenVersionToComponent({ orderData, orderId, kioskId, dietaryPreferences }: any) {
	switch (orderData?.kitchenVersion) {
		case KitchenVersions.V4:
			return (
				<TrackOrderV4 initialOrderData={orderData} orderId={orderId} kioskId={kioskId} dietaryPreferences={dietaryPreferences} />
			);

		case KitchenVersions.V3:
		default:
			return (
				<TrackOrderV3 initialOrderData={orderData} orderId={orderId} kioskId={kioskId} dietaryPreferences={dietaryPreferences} />
			);
	}
}

function renderChildren({ loading, error, initialOrderData, dietaryPreferences, orderId, kioskId, orderDataLoading }: any) {
	return (
		<AsyncContentWrapper isLoading={loading || orderDataLoading} error={error} errorComponent={() => <ErrorComponent code={error} />}>
			{mapKitchenVersionToComponent({ orderData: initialOrderData, orderId, kioskId, dietaryPreferences })}
		</AsyncContentWrapper>
	);
}

function renderDropdown({ activeOrdersData, kioskId, activeOrderId, onSelect, loading, orderDataLoading } : any) {
	const restaurantActiveOrders = activeOrdersData?.[kioskId];
	const activeOrderCode = getOrderCode(activeOrderId);
	let dropdownList: any[] = [], activeOrdersMessage = "", hasActiveOrders = false;
	if (!isEmptyObject(restaurantActiveOrders)) {
		const { activeOrderIds, activeOrderText } = restaurantActiveOrders;
		if(!isEmptyObject(activeOrderIds)) {
			hasActiveOrders = true;
			activeOrdersMessage = activeOrderText;
			dropdownList = Object.keys(activeOrderIds).map((orderId) => {
				const orderCode = getOrderCode(orderId);
				return { value: orderId, label: `Order #${orderCode}` }
			});
		}
	}
	return (
		<AsyncContentWrapper>
			<Box>
				{ hasActiveOrders && activeOrdersMessage ? <PrimaryTextLight>{activeOrdersMessage}</PrimaryTextLight> : "" }
				<Dropdown
					isDisabled={loading || orderDataLoading}
					items={dropdownList}
					selectedItem={{ value: activeOrderId, label: `Order #${activeOrderCode}` }}
					onSelect={(({ value }: any) => onSelect(value))}
				/>
			</Box>
		</AsyncContentWrapper>
	);
}

const TrackOrderParent = (props: any) => {
	const { kioskId, id: orderId } = props.match.params;
	const { dietaryPreferences, setUserData, activeOrdersData } = props;

	const [activeOrderId, setActiveOrderId] = useState<string>(orderId);
	const [initialOrderData, setInitialOrderData] = useState<any>({});
	const [userDataLoaded, setUserDataLoaded] = useState<boolean>(false); //TODO: Move this to Global state.

	const [loading, setLoading] = useState<boolean>(true);
	const [orderDataLoading, setOrderDataLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");

	async function getOrderData(orderId: string, updateCall: boolean = false) {
		if (!orderId) {
			setError("");
			return;
		}

		setOrderDataLoading(true)

		const response = await OrderService.trackOrder(orderId);
		const { data, error, errorCode } = response;
		if (data) {
			setInitialOrderData(data);
		}
		if (error) {
			setError(errorCode);
		}
		setOrderDataLoading(false);
	}

	async function getUserPreferences() {
		const response = await UserService.getUserPreferences();
		const { data, error, errorCode } = response;
		if (data) {
			const { allergens, activeOrdersData } = data;
			setUserData({ allergens, activeOrdersData });
			setUserDataLoaded(true);
		}
		if (error) {
			setError(errorCode);
		}
	}

	function fetchRequiredData(activeOrderId: string, userDataLoaded: boolean) {
		let promises: any[] = [];

		if (!userDataLoaded) {
			const userPrefPromise = getUserPreferences();
			promises.push(userPrefPromise);
		}

		const orderDataPromise = getOrderData(activeOrderId);
		promises.push(orderDataPromise);

		Promise.all(promises).then(() => {
			setLoading(false);
		});
	}

	async function onPageLoad() {
		Analytics.trackPage(TRACK_PAGE_CATEGORIES.PAGE_LOAD, "Track Order");
	}

	useEffect(() => {
		onPageLoad();
	}, []);

	useEffect(() => {
		fetchRequiredData(activeOrderId, userDataLoaded);
	}, [activeOrderId]);

	useEffect(() => {
		window.history.replaceState(null, "New Page Title", `/kiosk/${kioskId}/order/track/${activeOrderId}`)
	}, [activeOrderId])

	return (
		<Page padding="0" >
			<PageHeader>
				<BackButton to={`/kiosk/${kioskId}?source=trackOrder`}>
					<BackButtonImg src={chevronBackOutline} />
				</BackButton>
				<PageTitle>Track Order</PageTitle>
				<HelpButton />
			</PageHeader>
			{renderDropdown({ activeOrdersData, kioskId, activeOrderId, onSelect: setActiveOrderId, loading, orderDataLoading })}
			{renderChildren({ loading, error, initialOrderData, dietaryPreferences, orderId: activeOrderId, kioskId, orderDataLoading })}
		</Page>
	);
};

//Redux mapping
const mapStateToProps = (state: any) => {
	const {
		User: { dietaryPreferences, activeOrdersData },
	} = state;
	return { dietaryPreferences, activeOrdersData };
};

const mapDispatchToProps = (dispatch: any) => ({
	...bindActionCreators(
		{
			setUserData,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(withNav(TrackOrderParent));
